import React, { useCallback, useRef, useState } from 'react';
import { WRS_MAX_DB, PCT_MAX } from './speechLogic';

const RE_COLOR = '#dc2626';
const LE_COLOR = '#2563eb';

/**
 * WrsLineGraph — line chart for Word Recognition Score.
 *
 * • X = Intensity (dB HL), 0..100
 * • Y = Score (%), 0..100
 * • Two overlays:
 *     - Right Ear = red filled CIRCLES connected by red line
 *     - Left  Ear = blue filled SQUARES connected by blue line
 * • Click at any (dB, %) writes onto the *active ear* at the nearest
 *   configured intensity row (snapped to nearest row from `intensities`).
 */
const WrsLineGraph = ({
  wrs,             // { right: {db: pct}, left: {db: pct} }
  intensities,     // [20,40,60,80,100] — snap targets
  activeEar = 'right',
  onSetValue,      // (ear, dbHL, percent) => void
  onSetEar,
  readOnly = false,
  width = 460, height = 260,
}) => {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const marginL = 42, marginR = 14, marginT = 18, marginB = 40;
  const plotW = width - marginL - marginR;
  const plotH = height - marginT - marginB;
  const xScale = useCallback((db) => marginL + (db * plotW) / WRS_MAX_DB, [plotW]);
  const yScale = useCallback((pct) => marginT + ((PCT_MAX - pct) * plotH) / PCT_MAX, [plotH]);
  const yInv = useCallback((py) => {
    const raw = PCT_MAX - ((py - marginT) * PCT_MAX) / plotH;
    return Math.max(0, Math.min(PCT_MAX, Math.round(raw / 2) * 2));
  }, [plotH]);

  const rows = Array.isArray(intensities) && intensities.length ? intensities : [20, 40, 60, 80, 100];
  const snapDb = (px) => {
    let bestIdx = 0, bestDx = Infinity;
    rows.forEach((db, i) => {
      const dx = Math.abs(xScale(db) - px);
      if (dx < bestDx) { bestDx = dx; bestIdx = i; }
    });
    return rows[bestIdx];
  };

  const toViewBox = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };

  const snap = (p) => {
    if (!p) return null;
    if (p.y < marginT - 4 || p.y > height - marginB + 4) return null;
    if (p.x < marginL - 6 || p.x > width - marginR + 6) return null;
    return { db: snapDb(p.x), pct: yInv(Math.max(marginT, Math.min(height - marginB, p.y))) };
  };

  const handleClick = (evt) => {
    if (readOnly) return;
    const s = snap(toViewBox(evt));
    if (!s) return;
    onSetValue && onSetValue(activeEar, s.db, s.pct);
  };

  const seriesPoints = (ear) => {
    const map = wrs?.[ear] || {};
    return rows
      .map((db) => {
        const v = map[String(db)];
        if (v === undefined || v === null || v === '' || !Number.isFinite(Number(v))) return null;
        return { db, pct: Number(v), x: xScale(db), y: yScale(Number(v)) };
      })
      .filter(Boolean);
  };
  const rePts = seriesPoints('right');
  const lePts = seriesPoints('left');
  const rePath = rePts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const lePath = lePts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const yTicks = [0, 20, 40, 60, 80, 100];

  return (
    <div className="w-full">
      {!readOnly && (
        <div className="flex items-center gap-1 mb-1" data-testid="wrs-controls">
          <span className="text-[10px] text-slate-500 font-semibold uppercase mr-1">Plot ear:</span>
          {['right', 'left'].map((e) => (
            <button key={e} type="button" data-testid={`wrs-ear-${e}`}
              onClick={() => onSetEar && onSetEar(e)}
              className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${activeEar === e ? 'text-white' : 'bg-slate-100 text-slate-600'}`}
              style={activeEar === e ? { backgroundColor: e === 'right' ? RE_COLOR : LE_COLOR } : {}}
            >{e === 'right' ? 'RE' : 'LE'}</button>
          ))}
          <span className="ml-3 text-[10px] text-slate-400">Click at (intensity, %) to plot</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-1 text-[10px] text-slate-600" data-testid="wrs-legend">
        <span className="inline-flex items-center gap-1">
          <svg width="14" height="14" aria-hidden><circle cx="7" cy="7" r="4" fill={RE_COLOR} /></svg>
          <span style={{ color: RE_COLOR }} className="font-semibold">Right Ear (RE)</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <svg width="14" height="14" aria-hidden><rect x="3" y="3" width="8" height="8" fill={LE_COLOR} /></svg>
          <span style={{ color: LE_COLOR }} className="font-semibold">Left Ear (LE)</span>
        </span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%" height={height}
        preserveAspectRatio="none"
        onClick={handleClick}
        onMouseMove={(e) => { const s = snap(toViewBox(e)); setHover(s); }}
        onMouseLeave={() => setHover(null)}
        data-testid="wrs-graph"
        style={{ cursor: readOnly ? 'default' : 'crosshair', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4, display: 'block' }}
      >
        {/* Y grid */}
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={marginL} x2={width - marginR} y1={yScale(t)} y2={yScale(t)} stroke="#e2e8f0" strokeWidth="0.5" />
            <text x={marginL - 6} y={yScale(t) + 3} fontSize="9" fill="#64748b" textAnchor="end">{t}</text>
          </g>
        ))}
        {/* X grid at row intensities */}
        {rows.map((db) => (
          <g key={db}>
            <line x1={xScale(db)} x2={xScale(db)} y1={marginT} y2={marginT + plotH} stroke="#f1f5f9" strokeWidth="0.5" />
            <text x={xScale(db)} y={marginT + plotH + 14} fontSize="9" fill="#334155" textAnchor="middle" fontWeight="600">{db}</text>
          </g>
        ))}
        <text x={marginL - 30} y={marginT + plotH / 2 - 20} fontSize="9" fill="#475569" transform={`rotate(-90, ${marginL - 30}, ${marginT + plotH / 2})`} textAnchor="middle">Score (%)</text>
        <text x={marginL + plotW / 2} y={height - 4} fontSize="9" fill="#475569" textAnchor="middle">Intensity (dB HL)</text>

        {/* RE line + filled circles */}
        {rePath && <path d={rePath} fill="none" stroke={RE_COLOR} strokeWidth="2" />}
        {rePts.map((p) => (
          <circle key={`re-${p.db}`} cx={p.x} cy={p.y} r="4.5" fill={RE_COLOR} data-testid={`wrs-point-re-${p.db}`} />
        ))}
        {/* LE line + filled squares */}
        {lePath && <path d={lePath} fill="none" stroke={LE_COLOR} strokeWidth="2" />}
        {lePts.map((p) => (
          <rect key={`le-${p.db}`} x={p.x - 4} y={p.y - 4} width="8" height="8" fill={LE_COLOR} data-testid={`wrs-point-le-${p.db}`} />
        ))}

        {/* Hover ghost */}
        {!readOnly && hover && (
          <g style={{ pointerEvents: 'none' }} data-testid="wrs-hover">
            <line x1={xScale(hover.db)} x2={xScale(hover.db)} y1={marginT} y2={marginT + plotH} stroke={activeEar === 'right' ? RE_COLOR : LE_COLOR} strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
            {activeEar === 'right'
              ? <circle cx={xScale(hover.db)} cy={yScale(hover.pct)} r="4.5" fill={RE_COLOR} opacity="0.65" />
              : <rect x={xScale(hover.db) - 4} y={yScale(hover.pct) - 4} width="8" height="8" fill={LE_COLOR} opacity="0.65" />
            }
            <rect x={xScale(hover.db) + 6} y={Math.max(marginT, yScale(hover.pct) - 10)} width="70" height="14" rx="2" fill="#0f172a" opacity="0.85" />
            <text x={xScale(hover.db) + 8} y={Math.max(marginT, yScale(hover.pct) - 10) + 10} fontSize="9" fill="#ffffff" fontWeight="600">
              {hover.db} · {hover.pct} %
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default WrsLineGraph;
